import axios from 'axios';
import { User, Organization, Customer, CustomerAlias, DataUpload, UploadProgress, SubscriptionTier, SubscriptionStatus, M365Status, ServiceMapping, ServiceProduct, CustomerServicesView, ServiceMappingSummary, ServiceType, PartnerTenant, PartnerStatus, PartnerSyncResult, CoveDevice, CoveStatus, CoveSyncResult } from '../types';

// Use relative path since frontend is served from the same port as backend
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // 10 minutes timeout for long-running operations like invoice imports
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for integration endpoints - they have their own auth
      const url = error.config?.url || '';
      const isIntegrationEndpoint = url.includes('/xero') || url.includes('/m365') || url.includes('/partner') || url.includes('/cove')|| url.includes('/sentinelone') || url.includes('/proxmox');
      
      if (!isIntegrationEndpoint) {
        localStorage.removeItem('token');
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const organizationsApi = {
  list: async (): Promise<Organization[]> => {
    const response = await api.get('/organizations');
    return response.data;
  },
  get: async (id: number): Promise<Organization> => {
    const response = await api.get(`/organizations/${id}`);
    return response.data;
  },
  create: async (data: { name: string; slug: string; description?: string | null; subscription_tier?: SubscriptionTier; subscription_status?: SubscriptionStatus; is_active?: boolean }): Promise<Organization> => {
    const response = await api.post('/organizations', data);
    return response.data;
  },
  update: async (id: number, data: { name?: string; slug?: string; description?: string | null; subscription_tier?: SubscriptionTier; subscription_status?: SubscriptionStatus; is_active?: boolean }): Promise<Organization> => {
    const response = await api.patch(`/organizations/${id}`, data);
    return response.data;
  },
  clearData: async (orgId: number, password: string, clearCustomers: boolean = true, clearInvoices: boolean = true): Promise<{
    organization_id: number;
    organization_name: string;
    deleted: {
      customers: number;
      customer_aliases: number;
      invoices: number;
      products: number;
      uploads: number;
    };
    message: string;
  }> => {
    const response = await api.delete(`/organizations/${orgId}/data`, {
      data: { password, clear_customers: clearCustomers, clear_invoices: clearInvoices },
    });
    return response.data;
  },
};

export const customersApi = {
  list: async (skip = 0, limit = 10000, search?: string): Promise<Customer[]> => {
    const params: any = { skip, limit };
    if (search) {
      params.search = search;
    }
    const response = await api.get('/customers', { params });
    return response.data;
  },
  get: async (id: number): Promise<Customer> => {
    const response = await api.get(`/customers/${id}`);
    return response.data;
  },
  create: async (data: Partial<Customer>): Promise<Customer> => {
    const response = await api.post('/customers', data);
    return response.data;
  },
  createAlias: async (customerId: number, aliasName: string, dataSource: string): Promise<CustomerAlias> => {
    const response = await api.post(`/customers/${customerId}/aliases`, {
      alias_name: aliasName,
      data_source: dataSource,
    });
    return response.data;
  },
  listAliases: async (customerId: number): Promise<CustomerAlias[]> => {
    const response = await api.get(`/customers/${customerId}/aliases`);
    return response.data;
  },
  deleteAlias: async (aliasId: number): Promise<void> => {
    await api.delete(`/aliases/${aliasId}`);
  },
  // Cloud ITS / WebCafe: which of our companies invoices this customer (shared by the whole org)
  // Persistent billing note (standing instructions; empty clears it)
  setNote: async (customerId: number, note: string | null): Promise<{
    customer_id: number; billing_note: string | null; billing_note_by: string | null; billing_note_at: string | null;
  }> => (await api.put(`/customers/${customerId}/note`, { note })).data,
  // Shared "hidden from Invoice Prep" flag (replaced the per-browser list 2026-10-22)
  setHidden: async (customerId: number, hidden: boolean): Promise<{ customer_id: number; hidden: boolean }> =>
    (await api.put(`/customers/${customerId}/hide`, { hidden })).data,
  setEntity: async (customerId: number, entity: 'cloudits' | 'webcafe' | null): Promise<{ customer_id: number; billing_entity: string | null }> =>
    (await api.put(`/customers/${customerId}/entity`, { entity })).data,
  // "Flag for Marius" on Invoice Prep (shared by the whole org)
  setFlag: async (customerId: number, data: { flagged: boolean; note?: string | null }): Promise<{
    customer_id: number; review_flag: boolean; review_note: string | null; review_flagged_by: string | null; review_flagged_at: string | null;
  }> => (await api.put(`/customers/${customerId}/flag`, data)).data,
};

export const uploadsApi = {
  list: async (skip = 0, limit = 100): Promise<DataUpload[]> => {
    const response = await api.get('/uploads', { params: { skip, limit } });
    return response.data;
  },
  get: async (id: number): Promise<DataUpload> => {
    const response = await api.get(`/uploads/${id}`);
    return response.data;
  },
  getProgress: async (id: number): Promise<UploadProgress> => {
    const response = await api.get(`/uploads/${id}/progress`);
    return response.data;
  },
  cancel: async (id: number): Promise<{ message: string; upload_id: number; status: string }> => {
    const response = await api.post(`/uploads/${id}/cancel`);
    return response.data;
  },
  upload: async (file: File, periodMonth: string, dataSource: string): Promise<DataUpload> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('period_month', periodMonth);
    formData.append('data_source', dataSource);
    
    const response = await api.post('/uploads', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/uploads/${id}`);
  },
};

export interface XeroConnectionStatus {
  connected: boolean;
  tenant_id: string | null;
  connected_at: string | null;
}

export interface XeroContact {
  ContactID: string;
  ContactStatus: string;
  Name: string;
  FirstName?: string;
  LastName?: string;
  EmailAddress?: string;
  IsSupplier?: boolean;
  IsCustomer?: boolean;
  CreatedDateUTC?: string;
  UpdatedDateUTC?: string;
}

export interface XeroInvoice {
  InvoiceID: string;
  InvoiceNumber?: string;
  Type: string;
  Status: string;
  Date?: string;
  DueDate?: string;
  CurrencyCode?: string;
  Contact?: {
    ContactID: string;
    Name: string;
  };
  LineItems?: Array<{
    LineItemID?: string;
    Description?: string;
    Quantity?: number;
    UnitAmount?: number;
    AccountCode?: string;
  }>;
  Total?: number;
  TotalTax?: number;
  AmountDue?: number;
  CreatedDateUTC?: string;
  UpdatedDateUTC?: string;
}

export const xeroApi = {
  getStatus: async (): Promise<XeroConnectionStatus> => {
    const response = await api.get('/xero/status');
    return response.data;
  },
  connect: async (): Promise<string> => {
    // Make authenticated request to get redirect URL
    // Request JSON response instead of redirect to avoid CORS issues
    const response = await api.get('/xero/connect', {
      params: { redirect: false }, // Request JSON response
    });
    
    // Return the redirect URL (don't navigate here - let the component open popup)
    if (response.data && response.data.redirect_url) {
      return response.data.redirect_url;
    } else {
      throw new Error('No redirect URL received from server');
    }
  },
  disconnect: async (password: string): Promise<void> => {
    await api.post('/xero/disconnect', { password });
  },
  getContacts: async (where?: string, order?: string): Promise<XeroContact[]> => {
    const response = await api.get('/xero/contacts', {
      params: { where, order },
    });
    return response.data.contacts || [];
  },
  getInvoices: async (where?: string, order?: string, page?: number): Promise<XeroInvoice[]> => {
    const response = await api.get('/xero/invoices', {
      params: { where, order, page },
    });
    return response.data.invoices || [];
  },
  importContacts: async (periodMonth: string): Promise<{ imported: number; errors: string[]; status?: string; message?: string; upload_id?: number }> => {
    // This will fetch contacts from Xero and create them as customers
    const response = await api.post('/xero/import/contacts', {
      period_month: periodMonth,
    });
    return response.data;
  },
  importInvoices: async (periodMonth: string, where?: string): Promise<{ imported: number; errors: string[]; status?: string; message?: string; upload_id?: number }> => {
    // This will fetch invoices from Xero and create product records
    // Returns immediately with status 'processing' for background processing
    const response = await api.post('/xero/import/invoices', {
      period_month: periodMonth,
      where,
    });
    return response.data;
  },
  resumeInvoiceImport: async (uploadId: number): Promise<{ upload_id: number; status: string; message: string; progress?: { processed: number; total: number } }> => {
    // Resume a paused or failed invoice import
    const response = await api.post(`/xero/import/invoices/${uploadId}/resume`);
    return response.data;
  },
  deleteAllInvoices: async (): Promise<{ deleted: number; products_deleted: number; message: string }> => {
    // Delete all Xero invoice imports for the current organization
    const response = await api.delete('/xero/invoices');
    return response.data;
  },

  // Smart sync endpoints
  previewSync: async (): Promise<{
    total_xero_contacts: number;
    new_customers: number;
    updated_customers: number;
    duplicates_found: number;
    preview_new: any[];
    preview_updates: any[];
    preview_duplicates: any[];
  }> => {
    const response = await api.get('/xero/sync/preview');
    return response.data;
  },

  syncCustomers: async (): Promise<{
    created: number;
    updated: number;
    duplicates_merged: number;
    errors: string[];
    message: string;
  }> => {
    const response = await api.post('/xero/sync/customers');
    return response.data;
  },

  findDuplicates: async (): Promise<{
    total_duplicate_groups: number;
    duplicates: any[];
  }> => {
    const response = await api.get('/xero/customers/duplicates');
    return response.data;
  },

  mergeCustomers: async (keepId: number, mergeIds: number[]): Promise<{
    message: string;
    products_moved: number;
    mappings_moved: number;
  }> => {
    const response = await api.post('/xero/customers/merge', {
      keep_id: keepId,
      merge_ids: mergeIds
    });
    return response.data;
  },

  // Xero Items/Products
  getItems: async (search?: string): Promise<any[]> => {
    const params = search ? { search } : {};
    const response = await api.get('/xero/items', { params });
    return response.data;
  },

  syncItems: async (): Promise<{
    total_items: number;
    created: number;
    updated: number;
    errors: string[];
    message: string;
  }> => {
    const response = await api.post('/xero/items/sync');
    return response.data;
  },

  linkItemToProducts: async (itemId: number, productIds: number[]): Promise<any> => {
    const response = await api.post(`/xero/items/${itemId}/link`, productIds);
    return response.data;
  },

  unlinkProduct: async (itemId: number, productId: number): Promise<any> => {
    const response = await api.delete(`/xero/items/${itemId}/unlink/${productId}`);
    return response.data;
  },

  getItemProducts: async (itemId: number): Promise<any> => {
    const response = await api.get(`/xero/items/${itemId}/products`);
    return response.data;
  },

  getUnlinkedProducts: async (serviceType?: string): Promise<any> => {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await api.get('/xero/unlinked-products', { params });
    return response.data;
  },
  // SKU to Xero Product Mapping
  getSkuMappings: async (search?: string, unmappedOnly?: boolean): Promise<any[]> => {
    const params: any = {};
    if (search) params.search = search;
    if (unmappedOnly) params.unmapped_only = true;
    const response = await api.get("/xero/sku-mappings", { params });
    return response.data;
  },

  mapSkuToXero: async (sku: string, xeroItemId: number): Promise<any> => {
    const response = await api.post("/xero/sku-mappings/" + encodeURIComponent(sku) + "/map/" + xeroItemId);
    return response.data;
  },

  unmapSku: async (sku: string): Promise<any> => {
    const response = await api.delete("/xero/sku-mappings/" + encodeURIComponent(sku) + "/unmap");
    return response.data;
  },

  bulkMapSkus: async (mappings: Array<{sku_part_number: string, xero_item_id: number}>): Promise<any> => {
    const response = await api.post("/xero/sku-mappings/bulk", mappings);
    return response.data;
  },

  getImportedProducts: async (skip = 0, limit = 100, periodMonth?: string): Promise<{
    products: Array<{
      id: number;
      upload_id: number;
      customer_name: string;
      product_name: string;
      product_sku?: string;
      quantity: number;
      invoice_number?: string;
      invoice_id?: string;
      invoice_date?: string;
      invoice_due_date?: string;
      currency_code?: string;
      unit_amount?: number;
      total?: number;
      created_at?: string;
    }>;
    total: number;
    skip: number;
    limit: number;
    period_month?: string;
    upload_count: number;
  }> => {
    const response = await api.get('/xero/imported/products', {
      params: { skip, limit, period_month: periodMonth },
    });
    return response.data;
  },
  getImportedCustomers: async (skip = 0, limit = 100): Promise<{
    customers: Array<{
      id: number;
      xero_customer_id: string;
      name: string;
      email?: string;
      status?: string;
      created_at?: string;
      updated_at?: string;
    }>;
    total: number;
    skip: number;
    limit: number;
  }> => {
    const response = await api.get('/xero/imported/customers', {
      params: { skip, limit },
    });
    return response.data;
  },
};

export const adminApi = {
  listUsers: async (skip = 0, limit = 100, includeInactive = false): Promise<User[]> => {
    const response = await api.get('/users', { params: { skip, limit, include_inactive: includeInactive } });
    return response.data;
  },
  getUser: async (id: number): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  createUser: async (data: { email: string; password: string; full_name?: string; organization_id: number; role?: string }): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data;
  },
  updateUser: async (id: number, data: { full_name?: string; role?: string; is_active?: boolean; organization_id?: number }): Promise<User> => {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },
  deleteUser: async (id: number, password: string): Promise<void> => {
    await api.delete(`/users/${id}`, { data: { password } });
  },
  restoreUser: async (id: number): Promise<User> => {
    const response = await api.post(`/users/${id}/restore`);
    return response.data;
  },
  setPassword: async (id: number, newPassword: string): Promise<User> => {
    const response = await api.post(`/users/${id}/set-password`, { new_password: newPassword });
    return response.data;
  },
};

export interface M365SettingsPayload {
  client_id: string;
  tenant_id: string;
  client_secret?: string;
  password: string;  // Required for step-up authentication
}

export const m365Api = {
  getStatus: async (): Promise<M365Status> => {
    const response = await api.get('/m365/status');
    return response.data;
  },
  saveSettings: async (payload: M365SettingsPayload): Promise<M365Status> => {
    const response = await api.post('/m365/settings', payload);
    return response.data;
  },
  connect: async (redirect = false): Promise<{ redirect_url?: string }> => {
    const response = await api.get('/m365/connect', { params: { redirect } });
    return response.data;
  },
  disconnect: async (password: string): Promise<void> => {
    await api.post('/m365/disconnect', { password });
  },
  refreshTokens: async (): Promise<M365Status> => {
    const response = await api.post('/m365/refresh');
    return response.data;
  },
  importUsage: async (
    periodMonth: string,
    workload?: string
  ): Promise<{ upload_id: number; status: string; message: string }> => {
    const response = await api.post('/m365/import', {
      period_month: periodMonth,
      workload,
    });
    return response.data;
  },
};

// Service Mappings API
export const serviceMappingsApi = {
  // Get unified customer services view
  getCustomerServices: async (customerId: number): Promise<CustomerServicesView> => {
    const response = await api.get(`/customers/${customerId}/services`);
    return response.data;
  },

  // List service mappings
  list: async (serviceType?: ServiceType, customerId?: number, skip = 0, limit = 100): Promise<ServiceMapping[]> => {
    const params: any = { skip, limit };
    if (serviceType) params.service_type = serviceType;
    if (customerId) params.customer_id = customerId;
    const response = await api.get('/service-mappings', { params });
    return response.data;
  },

  // Create service mapping
  create: async (data: {
    customer_id: number;
    service_type: ServiceType;
    external_id?: string;
    external_name?: string;
    is_active?: boolean;
    metadata?: Record<string, any>;
  }): Promise<ServiceMapping> => {
    const response = await api.post('/service-mappings', data);
    return response.data;
  },

  // Update service mapping
  update: async (mappingId: number, data: {
    external_id?: string;
    external_name?: string;
    is_active?: boolean;
    metadata?: Record<string, any>;
  }): Promise<ServiceMapping> => {
    const response = await api.put(`/service-mappings/${mappingId}`, data);
    return response.data;
  },

  // Delete service mapping
  delete: async (mappingId: number): Promise<void> => {
    await api.delete(`/service-mappings/${mappingId}`);
  },

  // Add product to mapping
  addProduct: async (mappingId: number, data: {
    product_sku?: string;
    product_name: string;
    quantity?: number;
    unit_price?: string;
    billing_cycle?: string;
    is_active?: boolean;
    extra_data?: Record<string, any>;
  }): Promise<ServiceProduct> => {
    const response = await api.post(`/service-mappings/${mappingId}/products`, data);
    return response.data;
  },

  // Update product
  updateProduct: async (productId: number, data: {
    product_sku?: string;
    product_name?: string;
    quantity?: number;
    unit_price?: string;
    billing_cycle?: string;
    is_active?: boolean;
    extra_data?: Record<string, any>;
  }): Promise<ServiceProduct> => {
    const response = await api.put(`/products/${productId}`, data);
    return response.data;
  },

  // Delete product
  deleteProduct: async (productId: number): Promise<void> => {
    await api.delete(`/products/${productId}`);
  },

  // Bulk create mappings
  bulkCreate: async (mappings: Array<{
    customer_id: number;
    service_type: ServiceType;
    external_id?: string;
    external_name?: string;
    is_active?: boolean;
    metadata?: Record<string, any>;
  }>): Promise<{ created: number; errors: string[] }> => {
    const response = await api.post('/service-mappings/bulk', { mappings });
    return response.data;
  },

  // Get summary
  getSummary: async (): Promise<ServiceMappingSummary> => {
    const response = await api.get('/service-mappings/summary');
    return response.data;
  },
};

// Partner Center API
export const partnerCenterApi = {
  // Save credentials and test connection
  saveSettings: async (data: {
    tenant_id: string;
    client_id: string;
    client_secret: string;
    password: string;
  }): Promise<PartnerStatus> => {
    const response = await api.post('/partner/settings', data);
    return response.data;
  },

  // Get connection status
  getStatus: async (): Promise<PartnerStatus> => {
    const response = await api.get('/partner/status');
    return response.data;
  },

  // List all partner tenants
  getTenants: async (): Promise<PartnerTenant[]> => {
    const response = await api.get('/partner/tenants');
    return response.data;
  },

  // Sync tenants to service mappings
  sync: async (): Promise<PartnerSyncResult> => {
    const response = await api.post('/partner/sync');
    return response.data;
  },

  // Licensed users per license for a tenant (client profile view)
  getLicensedUsers: async (tenantId: string, refresh = false): Promise<any> => {
    const response = await api.get(`/partner/tenants/${tenantId}/licensed-users`, {
      params: refresh ? { refresh: true } : {},
    });
    return response.data;
  },

  // Manually map a tenant to a customer
  mapTenant: async (tenantId: string, customerId: number): Promise<{ message: string; mapping_id: number }> => {
    const response = await api.post('/partner/map-tenant', null, {
      params: { tenant_id: tenantId, customer_id: customerId }
    });
    return response.data;
  },

  // Fetch subscriptions for all mapped tenants
  fetchSubscriptions: async (): Promise<{
    total_mappings: number;
    successful: number;
    failed: number;
    total_subscriptions: number;
    products_created: number;
    errors: string[];
  }> => {
    const response = await api.post('/partner/fetch-subscriptions');
    return response.data;
  },

  // Get Secure App Model auth status
  getAuthStatus: async (): Promise<{
    secure_auth: boolean;
    credentials_configured: boolean;
    message: string;
  }> => {
    const response = await api.get('/partner/auth/status');
    return response.data;
  },

  // Import all licenses using Secure App Model
  importLicenses: async (period?: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.post('/partner/licenses/import', null, {
      params: period ? { period } : {}
    });
    return response.data;
  },

  // Get import progress
  getImportStatus: async (): Promise<{
    status: string;
    current: number;
    total: number;
    message: string;
    accessible: number;
    inaccessible: number;
  }> => {
    const response = await api.get('/partner/licenses/import/status');
    return response.data;
  },
};

// Cove API
export const coveApi = {
  // Get connection status
  getStatus: async (): Promise<CoveStatus> => {
    const response = await api.get('/cove/status');
    return response.data;
  },

  // Save credentials and test connection
  saveSettings: async (data: {
    partner: string;
    username: string;
    api_password: string;
    user_password: string;
  }): Promise<CoveStatus> => {
    const response = await api.post('/cove/settings', data);
    return response.data;
  },

  // List all devices with device names
  getDevices: async (refresh?: boolean): Promise<CoveDevice[]> => {
    const response = await api.get('/cove/devices', { params: refresh ? { refresh: 'true' } : {} });
    return response.data;
  },

  // Auto-sync by matching partner names
  sync: async (): Promise<CoveSyncResult> => {
    const response = await api.post('/cove/sync');
    return response.data;
  },

  // Map a device to a customer (with billing category + optional price override)
  mapDevice: async (
    device: CoveDevice,
    customerId: number,
    opts?: { category?: string; unitPrice?: string }
  ): Promise<{ message: string; mapping_id: number }> => {
    const response = await api.post('/cove/map-device', {
      device_id: device.id,
      customer_id: customerId,
      device_name: device.name,
      partner_name: device.partner_name,
      partner_id: device.partner_id,
      category: opts?.category ?? device.category,
      mailbox_count: device.mailbox_count,
      unit_price: opts?.unitPrice,
    });
    return response.data;
  },

  // Update category / price of an already-mapped device
  updatePricing: async (
    deviceId: number,
    opts: { category?: string; unitPrice?: string; mailboxCount?: number }
  ): Promise<{ message: string; mapping_id: number }> => {
    const response = await api.post('/cove/device-pricing', {
      device_id: deviceId,
      category: opts.category,
      unit_price: opts.unitPrice,
      mailbox_count: opts.mailboxCount,
    });
    return response.data;
  },

  // Bulk map multiple devices to a customer (optional category/price override for all)
  bulkMapDevices: async (
    devices: CoveDevice[],
    customerId: number,
    opts?: { category?: string; unitPrice?: string }
  ): Promise<{ message: string; created: number; updated: number; errors: string[] }> => {
    const response = await api.post('/cove/map-devices-bulk', {
      customer_id: customerId,
      category: opts?.category,
      unit_price: opts?.unitPrice,
      devices: devices.map(d => ({
        device_id: d.id,
        device_name: d.name,
        partner_name: d.partner_name,
        partner_id: d.partner_id,
        category: d.category,
        mailbox_count: d.mailbox_count,
      }))
    });
    return response.data;
  },

  // Unmap a device
  unmapDevice: async (deviceId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/cove/map-device/${deviceId}`);
    return response.data;
  },
};

// Billing API
export const billingApi = {
  // "Sync from integrations": rebuild billing lines from the integration caches only (no external API calls)
  refreshLines: async (): Promise<{ ok: boolean; seconds: number; integrations: Record<string, any>; errors: Record<string, string> }> =>
    (await api.post('/billing/refresh-lines')).data,
  // Get billing overview for all customers
  getOverview: async (filterStatus?: string, search?: string, period?: string): Promise<any> => {
    const params: any = {};
    if (filterStatus) params.filter_status = filterStatus;
    if (search) params.search = search;
    if (period) params.period = period;
    const response = await api.get('/billing/overview', { params });
    return response.data;
  },

  // Download the invoice-prep line items as a CSV (auth via axios, then trigger save)
  downloadCsv: async (onlyReady?: boolean): Promise<void> => {
    const response = await api.get('/billing/export.csv', {
      params: onlyReady ? { only_ready: true } : {},
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-${new Date().toISOString().slice(0, 7)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Get detailed billing info for a customer
  // "Invoice processed" recon check (live view checklist, per customer + month)
  setProcessed: async (customerId: number, data: { period: string; checked: boolean; note?: string | null }): Promise<any> =>
    (await api.put(`/billing/checks/${customerId}`, data)).data,
  getCustomerDetails: async (customerId: number): Promise<any> => {
    const response = await api.get(`/billing/customer/${customerId}/details`);
    return response.data;
  },

  // Manual services (non-integration line items)
  addManualProduct: async (customerId: number, data: {
    product_name: string; product_sku?: string; quantity: number;
    unit_price?: string; billing_cycle: string;
  }): Promise<any> => {
    const response = await api.post(`/billing/customer/${customerId}/manual-products`, data);
    return response.data;
  },
  updateManualProduct: async (productId: number, data: {
    product_name: string; product_sku?: string; quantity: number;
    unit_price?: string; billing_cycle: string;
  }): Promise<any> => {
    const response = await api.put(`/billing/manual-products/${productId}`, data);
    return response.data;
  },
  deleteManualProduct: async (productId: number): Promise<any> => {
    const response = await api.delete(`/billing/manual-products/${productId}`);
    return response.data;
  },

  // Price book: catalog with cost (integration-refreshed or manual), default
  // sell, per-client prices and HaloPSA item mapping
  getPriceBook: async (): Promise<any[]> => {
    const response = await api.get('/billing/price-book');
    return response.data;
  },
  updatePriceBookItem: async (data: {
    service_type: string; item_name: string;
    selling_price?: string | null; cost_price?: string | null; cost_source?: string;
    halo_item_id?: string | null; halo_item_name?: string | null;
  }): Promise<any> => {
    const response = await api.put('/billing/price-book/item', data);
    return response.data;
  },
  deletePriceBookItem: async (service_type: string, item_name: string): Promise<any> => {
    const response = await api.delete('/billing/price-book/item', { params: { service_type, item_name } });
    return response.data;
  },
  getClientPrices: async (service_type: string, item_name: string): Promise<any[]> => {
    const response = await api.get('/billing/price-book/client-prices', { params: { service_type, item_name } });
    return response.data;
  },
  setClientPrice: async (data: {
    service_type: string; item_name: string; customer_id: number; selling_price?: string | null;
  }): Promise<any> => {
    const response = await api.put('/billing/price-book/client-price', data);
    return response.data;
  },
  importPriceBook: async (service_type = 'cloudblue'): Promise<any> => {
    const response = await api.post('/billing/price-book/import', null, { params: { service_type } });
    return response.data;
  },

  // Get billing summary
  getSummary: async (): Promise<any> => {
    const response = await api.get('/billing/summary');
    return response.data;
  },

  // Trigger full sync
  syncAll: async (): Promise<any> => {
    const response = await api.post('/billing/sync-all');
    return response.data;
  },
};

// Billing runs - "bill now for the month": full sync -> frozen snapshot of every
// customer's billable lines, then per-customer invoiced state off that snapshot.
export interface BillingRunInfo {
  id: number;
  period: string;
  period_label: string;
  status: 'syncing' | 'snapshotting' | 'open' | 'error';
  message: string | null;
  created_at: string | null;
  created_by: string | null;
  sync_started_at: string | null;
  sync_finished_at: string | null;
  snapshot_at: string | null;
  sync_summary: Record<string, { status: string; items?: number; message?: string; log_id?: number; billable?: boolean }>;
  sync_failed: string[];
  sync_failed_billable: string[];
  customer_count: number;
  line_count: number;
  grand_total: string;
  cost_total: string;
  invoiced_count: number;
  invoiced_total: string;
  outstanding_count: number;
  outstanding_total: string;
  complete: boolean;
  locked: boolean;
}

export const billingRunsApi = {
  list: async (limit = 24): Promise<BillingRunInfo[]> => {
    const response = await api.get('/billing/runs', { params: { limit } });
    return response.data;
  },
  getForPeriod: async (period: string): Promise<{ run: BillingRunInfo | null; period: string; period_label: string }> => {
    const response = await api.get(`/billing/runs/period/${period}`);
    return response.data;
  },
  get: async (runId: number): Promise<BillingRunInfo> => {
    const response = await api.get(`/billing/runs/${runId}`);
    return response.data;
  },
  start: async (period: string, force = false): Promise<{ success: boolean; run: BillingRunInfo }> => {
    const response = await api.post('/billing/runs', { period, force });
    return response.data;
  },
  remove: async (runId: number): Promise<any> => {
    const response = await api.delete(`/billing/runs/${runId}`);
    return response.data;
  },
  getOverview: async (runId: number, filterStatus?: string, search?: string): Promise<any> => {
    const params: any = {};
    if (filterStatus) params.filter_status = filterStatus;
    if (search) params.search = search;
    const response = await api.get(`/billing/runs/${runId}/overview`, { params });
    return response.data;
  },
  getCustomerDetails: async (runId: number, customerId: number): Promise<any> => {
    const response = await api.get(`/billing/runs/${runId}/customers/${customerId}/details`);
    return response.data;
  },
  markCustomer: async (runId: number, customerId: number, data: {
    status: 'invoiced' | 'pending'; invoice_ref?: string | null; notes?: string | null;
  }): Promise<{ success: boolean; customer: any; run: BillingRunInfo }> => {
    const response = await api.put(`/billing/runs/${runId}/customers/${customerId}`, data);
    return response.data;
  },
  getIntegrations: async (runId: number): Promise<{ run: BillingRunInfo; integrations: any[] }> => {
    const response = await api.get(`/billing/runs/${runId}/integrations`);
    return response.data;
  },
};

// Integrations API - status, manual refresh, sync logs, settings
export const integrationsApi = {
  getStatus: async (): Promise<any[]> => {
    const response = await api.get('/integrations/status');
    return response.data;
  },
  sync: async (integration: string): Promise<any> => {
    const response = await api.post(`/integrations/${integration}/sync`);
    return response.data;
  },
  syncAll: async (): Promise<any> => {
    const response = await api.post('/integrations/sync-all');
    return response.data;
  },
  getLog: async (logId: number): Promise<any> => {
    const response = await api.get(`/integrations/logs/${logId}`);
    return response.data;
  },
  getLogs: async (integration: string, limit = 20): Promise<any[]> => {
    const response = await api.get(`/integrations/${integration}/logs`, { params: { limit } });
    return response.data;
  },
  // Per-run change detail (CloudBlue: which client changed which licenses)
  getLogChanges: async (logId: number): Promise<any> => {
    const response = await api.get(`/integrations/logs/${logId}/changes`);
    return response.data;
  },
  getNightlySummary: async (): Promise<any> => {
    const response = await api.get('/integrations/nightly/summary');
    return response.data;
  },
  updateSettings: async (integration: string, data: { nightly_enabled?: boolean; settings?: any }): Promise<any> => {
    const response = await api.put(`/integrations/${integration}/settings`, data);
    return response.data;
  },
};

export const dashboardApi = {
  get: async () => {
    const r = await api.get("/dashboard");
    return r.data;
  },
};
export default api;


// WHMCS API
export const whmcsApi = {
  getStatus: () => api.get('/whmcs/status'),
  syncClients: () => api.post('/whmcs/sync/clients'),
  syncDomains: () => api.post('/whmcs/sync/domains'),
  syncProducts: () => api.post('/whmcs/sync/products'),
  autoMap: () => api.post('/whmcs/auto-map'),

  getClients: (search?: string, unmappedOnly?: boolean) =>
    api.get('/whmcs/clients', { params: { search, unmapped_only: unmappedOnly } }),
  getDomains: (search?: string, unmappedOnly?: boolean) =>
    api.get('/whmcs/domains', { params: { search, unmapped_only: unmappedOnly } }),
  getProducts: (search?: string, unmappedOnly?: boolean) =>
    api.get('/whmcs/products', { params: { search, unmapped_only: unmappedOnly } }),

  getSyncStatus: () => api.get('/whmcs/sync-status'),

  mapClient: (clientId: number, customerId: number) =>
    api.post(`/whmcs/clients/${clientId}/map`, null, { params: { customer_id: customerId } }),
  mapDomain: (domainId: number, customerId: number, unitPrice?: string) =>
    api.post(`/whmcs/domains/${domainId}/map`, { customer_id: customerId, unit_price: unitPrice }),
  mapProduct: (productId: number, customerId: number, unitPrice?: string) =>
    api.post(`/whmcs/products/${productId}/map`, { customer_id: customerId, unit_price: unitPrice }),

  updatePricing: (kind: 'product' | 'domain', whmcsId: number, opts: { unitPrice?: string; billingCycle?: string }) =>
    api.post('/whmcs/item-pricing', { kind, whmcs_id: whmcsId, unit_price: opts.unitPrice, billing_cycle: opts.billingCycle }),

  unmapClient: (clientId: number) => api.delete(`/whmcs/clients/${clientId}/unmap`),
  unmapDomain: (domainId: number) => api.delete(`/whmcs/domains/${domainId}/unmap`),
  unmapProduct: (productId: number) => api.delete(`/whmcs/products/${productId}/unmap`),
};

// Bulk mapping
export const whmcsBulkApi = {
  bulkMapClients: (itemIds: number[], customerId: number) =>
    api.post('/whmcs/clients/bulk-map', { item_ids: itemIds, customer_id: customerId }),
  bulkMapDomains: (itemIds: number[], customerId: number, unitPrice?: string) =>
    api.post('/whmcs/domains/bulk-map', { item_ids: itemIds, customer_id: customerId, unit_price: unitPrice }),
  bulkMapProducts: (itemIds: number[], customerId: number, unitPrice?: string) =>
    api.post('/whmcs/products/bulk-map', { item_ids: itemIds, customer_id: customerId, unit_price: unitPrice }),
};

// Splynx (ISP billing) API
export const splynxApi = {
  getStatus: async (): Promise<import('../types').SplynxStatus> => {
    const response = await api.get('/splynx/status');
    return response.data;
  },
  sync: async (): Promise<{
    success: boolean; synced: number; removed: number;
    services_accessible: boolean; service_kinds: Record<string, 'ok' | 'forbidden' | 'error'>;
    services_synced: number; products_refreshed: number; errors: string[];
  }> => {
    const response = await api.post('/splynx/sync');
    return response.data;
  },
  getCustomers: async (opts?: { search?: string; status?: string; unmappedOnly?: boolean }):
    Promise<import('../types').SplynxCustomerRow[]> => {
    const response = await api.get('/splynx/customers', {
      params: {
        search: opts?.search || undefined,
        status: opts?.status || undefined,
        unmapped_only: opts?.unmappedOnly || undefined,
      },
    });
    return response.data;
  },
  getPackages: async (): Promise<import('../types').SplynxPackage[]> => (await api.get('/splynx/packages')).data,
  getCustomerDetail: async (id: number): Promise<import('../types').SplynxCustomerDetail> => {
    const response = await api.get(`/splynx/customers/${id}/services`);
    return response.data;
  },
  mapCustomer: async (id: number, customerId: number, unitPrice?: string):
    Promise<{ success: boolean; message: string; mapping_id: number }> => {
    const response = await api.post(`/splynx/customers/${id}/map`, {
      customer_id: customerId, unit_price: unitPrice || undefined,
    });
    return response.data;
  },
  unmapCustomer: async (id: number): Promise<{ success: boolean }> => {
    const response = await api.delete(`/splynx/customers/${id}/unmap`);
    return response.data;
  },
  bulkMap: async (itemIds: number[], customerId: number):
    Promise<{ success: boolean; mapped: number; customer: string }> => {
    const response = await api.post('/splynx/bulk-map', { item_ids: itemIds, customer_id: customerId });
    return response.data;
  },
  autoMap: async (): Promise<{ matched: number; created: number; unmatched: number }> => {
    const response = await api.post('/splynx/auto-map');
    return response.data;
  },
  updateItemPricing: async (productId: number, unitPrice?: string):
    Promise<{ success: boolean; unit_price: string | null; price_override: boolean }> => {
    const response = await api.post('/splynx/item-pricing', {
      product_id: productId, unit_price: unitPrice || undefined,
    });
    return response.data;
  },
};

// SentinelOne API
export const sentineloneApi = {
  getStatus: async () => {
    const response = await api.get('/sentinelone/status');
    return response.data;
  },
  getSites: async () => {
    const response = await api.get('/sentinelone/sites');
    return response.data;
  },
  getAgents: async (siteId: string) => {
    const response = await api.get('/sentinelone/sites/' + siteId + '/agents');
    return response.data;
  },
  getThreats: async () => {
    const response = await api.get('/sentinelone/threats');
    return response.data;
  },
  sync: async () => {
    const response = await api.post('/sentinelone/sync');
    return response.data;
  },
  testConnection: async () => {
    const response = await api.post('/sentinelone/test');
    return response.data;
  },
  getConfig: async (): Promise<{ source: 'stored' | 'env' | null; server: string; token_hint: string | null; saved_at: string | null }> =>
    (await api.get('/sentinelone/config')).data,
  saveConfig: async (data: { api_token: string; server?: string }): Promise<any> =>
    (await api.put('/sentinelone/config', data)).data,
  deleteConfig: async (): Promise<any> => (await api.delete('/sentinelone/config')).data,
  mapSite: async (siteId: string, customerId: number, siteName?: string) => {
    const response = await api.post('/sentinelone/map-site', {
      site_id: siteId,
      customer_id: customerId,
      site_name: siteName,
    });
    return response.data;
  },
  unmapSite: async (siteId: string) => {
    const response = await api.delete('/sentinelone/map-site/' + siteId);
    return response.data;
  },
};

// Proxmox VE API
export const proxmoxApi = {
  getStatus: async () => {
    const response = await api.get('/proxmox/status');
    return response.data;
  },
  getNodes: async () => {
    const response = await api.get('/proxmox/nodes');
    return response.data;
  },
  getVMs: async () => {
    const response = await api.get('/proxmox/vms');
    return response.data;
  },
  getNodeVMs: async (node: string) => {
    const response = await api.get('/proxmox/nodes/' + node + '/vms');
    return response.data;
  },
  mapVM: async (
    vmId: string,
    customerId: number,
    opts?: {
      vmName?: string;
      cpus?: number;
      memoryGb?: number;
      diskGb?: number;
      vcpuPrice?: string;
      vmemPrice?: string;
      ssdPrice?: string;
      billingCycle?: string;
    }
  ) => {
    const response = await api.post('/proxmox/map-vm', {
      vm_id: vmId,
      customer_id: customerId,
      vm_name: opts?.vmName,
      cpus: opts?.cpus,
      memory_gb: opts?.memoryGb,
      disk_gb: opts?.diskGb,
      vcpu_price: opts?.vcpuPrice,
      vmem_price: opts?.vmemPrice,
      ssd_price: opts?.ssdPrice,
      billing_cycle: opts?.billingCycle ?? 'monthly',
    });
    return response.data;
  },
  unmapVM: async (vmId: string) => {
    const response = await api.delete(`/proxmox/map-vm/${vmId}`);
    return response.data;
  },
  sync: async () => {
    const response = await api.post('/proxmox/sync');
    return response.data;
  },
};

export const expensesApi = {
  month: async (period: string, includeArchived = false) => {
    const response = await api.get(`/expenses/month/${period}`, {
      params: includeArchived ? { include_archived: true } : {},
    });
    return response.data;
  },
  history: async (months = 12) => {
    const response = await api.get('/expenses/history', { params: { months } });
    return response.data;
  },
  grid: async (year: number, includeArchived = false) => {
    const response = await api.get(`/expenses/grid/${year}`, {
      params: includeArchived ? { include_archived: true } : {},
    });
    return response.data;
  },
  create: async (data: {
    name: string;
    vendor?: string | null;
    category?: string | null;
    expected_amount: number;
    billing_cycle?: 'monthly' | 'annual';
    due_month?: number | null;
    due_day?: number | null;
    start_period?: string | null;
    payment_method?: string | null;
    notes?: string | null;
  }) => {
    const response = await api.post('/expenses', data);
    return response.data;
  },
  update: async (id: number, data: Record<string, any>) => {
    const response = await api.put(`/expenses/${id}`, data);
    return response.data;
  },
  remove: async (id: number, hard = false) => {
    const response = await api.delete(`/expenses/${id}`, {
      params: hard ? { hard: true } : {},
    });
    return response.data;
  },
  setPayment: async (
    id: number,
    period: string,
    data: { is_paid?: boolean; amount_paid?: number | null; paid_date?: string | null; notes?: string | null }
  ) => {
    const response = await api.put(`/expenses/${id}/payments/${period}`, data);
    return response.data;
  },
  clearPayment: async (id: number, period: string) => {
    const response = await api.delete(`/expenses/${id}/payments/${period}`);
    return response.data;
  },
  categories: async () => {
    const response = await api.get('/expenses/categories');
    return response.data;
  },
  createCategory: async (name: string) => {
    const response = await api.post('/expenses/categories', { name });
    return response.data;
  },
  renameCategory: async (id: number, name: string) => {
    const response = await api.put(`/expenses/categories/${id}`, { name });
    return response.data;
  },
  deleteCategory: async (id: number, reassignTo?: number | null) => {
    const response = await api.delete(`/expenses/categories/${id}`, {
      params: reassignTo != null ? { reassign_to: reassignTo } : {},
    });
    return response.data;
  },
};

// ── HaloPSA push: mapping (targets / line links / import) + billing-run push ──
export interface HaloCatalogueItem {
  halo_item_id: number; name: string; group_name: string | null; term: string | null; is_recurring: boolean;
  base_price: string | null; recurring_price: string | null; cost_price: string | null;
  accounts_id: string | null; active: boolean;
}

export const haloApi = {
  status: async (): Promise<any> => (await api.get('/halo/status')).data,
  suggestTargets: async (): Promise<any> => (await api.post('/halo/targets/suggest')).data,
  targets: async (status?: string): Promise<any[]> =>
    (await api.get('/halo/targets', { params: status ? { status } : {} })).data,
  updateTarget: async (id: number, data: { status: string; customer_id?: number; is_primary?: boolean }): Promise<any> =>
    (await api.put(`/halo/targets/${id}`, data)).data,
  confirmAll: async (body: { min_score?: number; target_ids?: number[] }): Promise<any> =>
    (await api.post('/halo/targets/confirm-all', body)).data,
  pinTarget: async (halo_ri_id: number, customer_id: number): Promise<any> =>
    (await api.post('/halo/targets/pin', { halo_ri_id, customer_id })).data,
  customerLines: async (customerId: number): Promise<any> => (await api.get(`/halo/customers/${customerId}/lines`)).data,
  linkLine: async (customerId: number, price_book_item_id: number, halo_line_id: number | null): Promise<any> =>
    (await api.post(`/halo/customers/${customerId}/lines/link`, { price_book_item_id, halo_line_id })).data,
  unlinkLine: async (customerId: number, price_book_item_id: number): Promise<any> =>
    (await api.delete(`/halo/customers/${customerId}/lines/link/${price_book_item_id}`)).data,
  importLines: async (customerId: number, halo_line_ids?: number[]): Promise<any> =>
    (await api.post(`/halo/customers/${customerId}/lines/import`, { halo_line_ids: halo_line_ids ?? null })).data,
  unmapped: async (refresh = false): Promise<any> => (await api.get('/halo/unmapped', { params: refresh ? { refresh: 1 } : {} })).data,
  unmappedCsv: async (): Promise<Blob> =>
    (await api.get('/halo/unmapped', { params: { format: 'csv' }, responseType: 'blob' })).data,
  items: async (q?: string): Promise<{ items: HaloCatalogueItem[]; count: number; synced_at: string | null }> =>
    (await api.get('/halo/items', { params: q ? { q } : {} })).data,
  syncItems: async (): Promise<{ items: number; vanished: number }> => (await api.post('/halo/items/sync')).data,
  itemMatching: async (): Promise<any> => (await api.get('/halo/items/matching')).data,
  acceptItem: async (price_book_item_id: number, halo_item_id: number | null): Promise<any> =>
    (await api.post('/halo/items/matching/accept', { price_book_item_id, halo_item_id })).data,
  acceptAllItems: async (min_score: number): Promise<any> => (await api.post('/halo/items/matching/accept-all', { min_score })).data,
  autoLinkLines: async (): Promise<any> => (await api.post('/halo/lines/auto-link')).data,
  runSummary: async (runId: number): Promise<any> => (await api.get(`/billing/runs/${runId}/halo`)).data,
  runCompute: async (runId: number): Promise<any> => (await api.post(`/billing/runs/${runId}/halo/compute`)).data,
  runCustomer: async (runId: number, customerId: number): Promise<any> =>
    (await api.get(`/billing/runs/${runId}/halo/${customerId}`)).data,
  runPush: async (runId: number, body: { customer_ids?: number[]; all_ready?: boolean }): Promise<any> =>
    (await api.post(`/billing/runs/${runId}/halo/push`, body)).data,
  runRollback: async (runId: number, itemId: number): Promise<any> =>
    (await api.post(`/billing/runs/${runId}/halo/rollback/${itemId}`)).data,
};

// ── PortaOne (PortaBilling reseller) voice usage ──
export const portaoneApi = {
  status: async (): Promise<any> => (await api.get('/portaone/status')).data,
  sync: async (): Promise<any> => (await api.post('/portaone/sync')).data,
  customers: async (periodEnd?: string): Promise<any> =>
    (await api.get('/portaone/customers', { params: periodEnd ? { period_end: periodEnd } : {} })).data,
  map: async (iCustomer: number, customerId: number): Promise<any> =>
    (await api.post(`/portaone/customers/${iCustomer}/map`, { customer_id: customerId })).data,
  unmap: async (iCustomer: number): Promise<any> => (await api.delete(`/portaone/customers/${iCustomer}/unmap`)).data,
  autoMap: async (): Promise<any> => (await api.post('/portaone/auto-map')).data,
  importReport: async (file: File): Promise<any> => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/portaone/import-report', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
};

// ── Redstor (cloud backup) ──
export const redstorApi = {
  status: async (): Promise<any> => (await api.get('/redstor/status')).data,
  getConfig: async (): Promise<any> => (await api.get('/redstor/config')).data,
  saveConfig: async (data: { api_key: string; url?: string; auth?: string }): Promise<any> => (await api.put('/redstor/config', data)).data,
  deleteConfig: async (): Promise<any> => (await api.delete('/redstor/config')).data,
  test: async (): Promise<any> => (await api.post('/redstor/test')).data,
  sync: async (): Promise<any> => (await api.post('/redstor/sync')).data,
  companies: async (): Promise<any> => (await api.get('/redstor/companies')).data,
  map: async (companyId: string, customerId: number): Promise<any> =>
    (await api.post(`/redstor/companies/${encodeURIComponent(companyId)}/map`, { customer_id: customerId })).data,
  unmap: async (companyId: string): Promise<any> => (await api.delete(`/redstor/companies/${encodeURIComponent(companyId)}/unmap`)).data,
  deleteCompany: async (companyId: string): Promise<any> => (await api.delete(`/redstor/companies/${encodeURIComponent(companyId)}`)).data,
  deleteProduct: async (companyId: string, productKey: string): Promise<any> =>
    (await api.delete(`/redstor/companies/${encodeURIComponent(companyId)}/products/${encodeURIComponent(productKey)}`)).data,
  autoMap: async (): Promise<any> => (await api.post('/redstor/auto-map')).data,
  importCsv: async (file: File): Promise<any> => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/redstor/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  manual: async (data: { company_name: string; product_name: string; company_id?: string; gb?: number; seats?: number; users?: number; workloads?: number }): Promise<any> =>
    (await api.post('/redstor/manual', data)).data,
  products: async (): Promise<any> => (await api.get('/redstor/products')).data,
  saveSettings: async (data: { bill_by?: Record<string, string>; exclude_trials?: boolean }): Promise<any> =>
    (await api.put('/redstor/settings', data)).data,
};
